import React, {useEffect} from 'react';
import { Button, Intent, Tooltip } from '@blueprintjs/core';
import DataSyncList from './components/DataSyncList';
import { useHistory } from 'react-router-dom';
import MainContainer from './MainContainer';
import { directoryStore } from '../../stores/DirectoryStore';
import { DataSyncStore, dataSyncStore } from '../../stores/DataSyncStore';
import { dataSyncMetricsStore } from '../../stores/DataSyncMetricsStore';
import { showToasts } from '../../lib/toaster';
import { useObserver } from 'mobx-react';
import { reaction } from 'mobx';

const DAILY_GRANULARITY = 'METRICS_GRANULARITY_DAILY';

const getLastNDaysRange = (days: number) => {
  const toEpoch = Math.floor(Date.now() / 1000);
  const fromEpoch = toEpoch - ((days - 1) * 24 * 60 * 60);
  return { fromEpoch, toEpoch };
};

export const DataSyncListPage = () => {
  const history = useHistory();

  const fetchDataSyncs = async () => {
    try {
      if (directoryStore.trackerId) {
        await dataSyncStore.fetch();
      }
    } catch (e) {
      showToasts([{intent: 'danger', message: `Failed to fetch data syncs. ${e}`}]);
    }
  };

  useEffect(() => {
    fetchDataSyncs();
  }, []);

  useEffect(() => {
    const disposer = reaction(
      () => ({
        trackerId: directoryStore.trackerId,
        loading: dataSyncStore.loading,
        rowStart: dataSyncStore.paging.rowStart,
        rowCount: dataSyncStore.paging.rowCount,
        searchFilter: dataSyncStore.searchFilter,
        filterByStatus: dataSyncStore.filterByStatus,
        sortField: dataSyncStore.sort.field,
        sortDirection: dataSyncStore.sort.direction,
        visibleIds: dataSyncStore.dataSyncs.map((item) => item.id),
      }),
      async ({ trackerId, loading, visibleIds }) => {
        if (!trackerId || loading || visibleIds.length === 0) {
          return;
        }

        try {
          const { fromEpoch, toEpoch } = getLastNDaysRange(7);
          await dataSyncMetricsStore.fetchMetrics(trackerId, visibleIds, {
            fromEpoch,
            toEpoch,
            granularity: DAILY_GRANULARITY,
          });
        } catch (e) {
          showToasts([{ intent: 'danger', message: `Failed to fetch metrics. ${e}` }]);
        }
      },
      { fireImmediately: true }
    );

    return () => {
      disposer();
    };
  }, []);

  const headerActions = useObserver(() => (
    <>
      <Button
        text="Refresh"
        intent={Intent.NONE}
        onClick={async () => {
          await fetchDataSyncs();
        }}
      />
      <Tooltip
        content={`Maximum number of data syncs is ${DataSyncStore.MAX_DATA_SYNCS}. Please delete existing syncs before creating a new one.`}
        disabled={dataSyncStore.dataSyncCount < DataSyncStore.MAX_DATA_SYNCS}
        placement="bottom"
      >
        <Button
          disabled={dataSyncStore.dataSyncCount >= DataSyncStore.MAX_DATA_SYNCS}
          intent={Intent.PRIMARY}
          text="New Sync"
          onClick={() => history.push('/data_syncs/new')}
        />
      </Tooltip>
    </>
  ));

  return (
    <MainContainer headerActions={headerActions} fixedHeight={true}>
      <DataSyncList dataSyncStore={dataSyncStore} metricsStore={dataSyncMetricsStore} />
    </MainContainer>
  );
};
